// Tidplansjämförelse: lägg in aktiviteter per område och tid, systemet hittar krockarna.
import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Download, LayoutGrid, Plus, Trash2 } from "lucide-react";

export interface PlannedActivity {
  id: string;
  name: string;
  actor: string;
  area: string;
  start: string;
  end: string;
  kind: string;
  /** Etapp i projektet (frivilligt) – gör det möjligt att filtrera krockar per etapp. */
  stage?: string;
  /** Varifrån raden kom när den importerades (etapp, arbetsberedning, arbetsområde). */
  source?: string;
}

/** Arbetstyper som är extra känsliga när de pågår samtidigt i samma utrymme. */
export const ACTIVITY_KINDS = [
  "Allmänt arbete",
  "Lyft",
  "Heta arbeten",
  "Rivning",
  "Damm/buller",
  "El",
  "Kemikalier",
  "Arbete på hög höjd",
  "Transport/schakt",
  "Ensamarbete/natt",
  "Trafikmiljö",
  "Vatten/våtarbete",
  "Asbest/sanering",
  "Pågående verksamhet/tredje man",
] as const;

const HIGH_RISK_PAIRS: Array<[string, string, string]> = [
  ["Lyft", "Allmänt arbete", "Last kan passera över personal i utrymmet."],
  ["Lyft", "Arbete på hög höjd", "Lyft över arbete på hög höjd – fallande last och trängsel."],
  ["Heta arbeten", "Kemikalier", "Antändningsrisk från heta arbeten intill brandfarlig vara."],
  ["Heta arbeten", "Rivning", "Brandrisk i rivningsdamm och dolda hålrum."],
  ["Rivning", "Allmänt arbete", "Damm, buller och rasrisk för andra som arbetar i utrymmet."],
  ["El", "Rivning", "Risk att spänningsförande delar rivs eller skadas."],
  ["Damm/buller", "Allmänt arbete", "Exponering för damm och buller för närliggande arbete."],
  ["Transport/schakt", "Allmänt arbete", "Fordonsrörelser i samma yta som gående personal."],
  // Utökade kombinationer
  ["El", "Vatten/våtarbete", "Vatten intill spänningsförande arbete – risk för strömgenomgång."],
  ["El", "Allmänt arbete", "Arbete intill spänningsförande delar utan avskärmning."],
  ["Ensamarbete/natt", "Lyft", "Ensamarbete intill tunga lyft – ingen som larmar vid olycka."],
  ["Ensamarbete/natt", "Rivning", "Ensamarbete i rivningsmiljö – rasrisk utan larmkedja."],
  ["Ensamarbete/natt", "Allmänt arbete", "Ensam- eller nattarbete parallellt – säkerställ larm och tillsyn."],
  ["Trafikmiljö", "Allmänt arbete", "Gående personal i samma yta som passerande trafik."],
  ["Trafikmiljö", "Transport/schakt", "Arbetsfordon och trafik i samma yta – påkörningsrisk."],
  ["Trafikmiljö", "Ensamarbete/natt", "Ensamarbete i trafikmiljö vid mörker – hög påkörningsrisk."],
  ["Asbest/sanering", "Allmänt arbete", "Andra arbeten pågår i saneringszon – spridning av asbestfibrer."],
  ["Asbest/sanering", "Rivning", "Rivning intill pågående asbestsanering – fibrer sprids."],
  ["Asbest/sanering", "Damm/buller", "Dammande arbete intill sanering – kontaminerad luft."],
  ["Pågående verksamhet/tredje man", "Rivning", "Tredje man vistas intill rivning – ras och damm."],
  ["Pågående verksamhet/tredje man", "Transport/schakt", "Fordonsrörelser där tredje man rör sig."],
  ["Pågående verksamhet/tredje man", "Heta arbeten", "Brandrisk i lokal med pågående verksamhet."],
  ["Pågående verksamhet/tredje man", "Lyft", "Lyft över yta där tredje man passerar."],
  ["Vatten/våtarbete", "Heta arbeten", "Fukt och heta arbeten – halka samt tätskikt i brandrisk."],
];

export interface DetectedConflict {
  a: PlannedActivity;
  b: PlannedActivity;
  from: string;
  to: string;
  severity: "medel" | "hög";
  reason: string;
}

const norm = (v: string) => v.trim().toLowerCase();

const sameArea = (a: string, b: string) => {
  const x = norm(a);
  const y = norm(b);
  if (!x || !y) return false;
  return x === y || x.includes(y) || y.includes(x);
};

const overlap = (a: PlannedActivity, b: PlannedActivity) => {
  if (!a.start || !a.end || !b.start || !b.end) return null;
  const from = a.start > b.start ? a.start : b.start;
  const to = a.end < b.end ? a.end : b.end;
  return from <= to ? { from, to } : null;
};

export const detectConflicts = (rows: PlannedActivity[]): DetectedConflict[] => {
  const out: DetectedConflict[] = [];
  for (let i = 0; i < rows.length; i++) {
    for (let j = i + 1; j < rows.length; j++) {
      const a = rows[i];
      const b = rows[j];
      if (!sameArea(a.area, b.area)) continue;
      const o = overlap(a, b);
      if (!o) continue;
      const pair = HIGH_RISK_PAIRS.find(
        ([x, y]) => (a.kind === x && b.kind === y) || (a.kind === y && b.kind === x)
      );
      out.push({
        a,
        b,
        from: o.from,
        to: o.to,
        severity: pair ? "hög" : "medel",
        reason:
          pair?.[2] ||
          "Två arbeten pågår samtidigt i samma utrymme – samordna tider eller avgränsa ytan.",
      });
    }
  }
  return out.sort((x, y) => (x.severity === y.severity ? 0 : x.severity === "hög" ? -1 : 1));
};

const newActivity = (): PlannedActivity => ({
  id: crypto.randomUUID(),
  name: "",
  actor: "",
  area: "",
  start: "",
  end: "",
  kind: "Allmänt arbete",
  stage: "",
});

const NO_STAGE = "__ingen__";

interface Props {
  activities: PlannedActivity[];
  onChange: (rows: PlannedActivity[]) => void;
  onAddConflict: (c: DetectedConflict) => void;
  /** Etappnamn i projektet – används för att märka aktiviteter. */
  stages?: string[];
  /** Hämtar etapper, arbetsberedningar och arbetsområden som aktivitetsrader. */
  onImport?: () => void;
  importing?: boolean;
  /** Lägger krocken som punkt i samordningsmötet. */
  onCreateCoordinationPoint?: (c: DetectedConflict) => void;
}

type SeverityFilter = "alla" | "hög" | "medel";

interface AreaSummary {
  area: string;
  total: number;
  high: number;
  actors: string[];
}

/** Summerar vilka utrymmen som driver flest krockar. */
export const summarizeAreas = (conflicts: DetectedConflict[]): AreaSummary[] => {
  const map = new Map<string, AreaSummary>();
  conflicts.forEach((c) => {
    const label = (c.a.area || c.b.area || "Ej angivet utrymme").trim() || "Ej angivet utrymme";
    const key = norm(label);
    const entry = map.get(key) || { area: label, total: 0, high: 0, actors: [] };
    entry.total += 1;
    if (c.severity === "hög") entry.high += 1;
    [c.a.actor, c.b.actor].forEach((actor) => {
      const name = actor?.trim();
      if (name && !entry.actors.includes(name)) entry.actors.push(name);
    });
    map.set(key, entry);
  });
  return Array.from(map.values()).sort(
    (x, y) => y.high - x.high || y.total - x.total || x.area.localeCompare(y.area, "sv")
  );
};

const conflictStages = (c: DetectedConflict) =>
  [c.a.stage, c.b.stage].map((s) => (s || "").trim()).filter(Boolean);

const ScheduleCompare: React.FC<Props> = ({
  activities,
  onChange,
  onAddConflict,
  stages = [],
  onImport,
  importing,
  onCreateCoordinationPoint,
}) => {
  const [showRows, setShowRows] = useState(activities.length > 0);
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>("alla");
  const [areaFilter, setAreaFilter] = useState<string | null>(null);
  const [stageFilter, setStageFilter] = useState<string>("alla");
  const conflicts = useMemo(() => detectConflicts(activities), [activities]);
  const areaSummary = useMemo(() => summarizeAreas(conflicts), [conflicts]);

  const stageOptions = useMemo(() => {
    const set = new Set<string>();
    stages.forEach((s) => s && set.add(s));
    activities.forEach((a) => a.stage && set.add(a.stage));
    return Array.from(set);
  }, [stages, activities]);

  const visibleConflicts = useMemo(
    () =>
      conflicts.filter((c) => {
        if (severityFilter !== "alla" && c.severity !== severityFilter) return false;
        if (areaFilter && !sameArea(c.a.area || c.b.area || "Ej angivet utrymme", areaFilter))
          return false;
        if (stageFilter !== "alla" && !conflictStages(c).includes(stageFilter)) return false;
        return true;
      }),
    [conflicts, severityFilter, areaFilter, stageFilter]
  );

  const highCount = conflicts.filter((c) => c.severity === "hög").length;
  const mediumCount = conflicts.length - highCount;

  const patch = (id: string, p: Partial<PlannedActivity>) =>
    onChange(activities.map((a) => (a.id === id ? { ...a, ...p } : a)));

  return (
    <Card>
      <CardHeader className="flex-col items-start gap-3 space-y-0 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <LayoutGrid className="h-4 w-4" /> Jämför tidplaner
          </CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            Lägg in vem som gör vad, var och när. Systemet visar automatiskt vilka arbeten som
            hamnar i samma utrymme samtidigt.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {onImport && (
            <Button size="sm" variant="secondary" onClick={onImport} disabled={importing}>
              <Download className="mr-1 h-4 w-4" />
              {importing ? "Hämtar…" : "Hämta från projektet"}
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              onChange([...activities, newActivity()]);
              setShowRows(true);
            }}
          >
            <Plus className="mr-1 h-4 w-4" /> Lägg till aktivitet
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {activities.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Inga aktiviteter inlagda. Lägg in minst två för att se krockar – eller hämta etapper,
            arbetsberedningar och arbetsområden från projektet.
          </p>
        )}

        {showRows &&
          activities.map((a) => (
            <div key={a.id} className="grid gap-3 rounded-md border p-3 sm:grid-cols-2">
              <div>
                <Label>Aktivitet</Label>
                <Input
                  value={a.name}
                  onChange={(e) => patch(a.id, { name: e.target.value })}
                  placeholder="T.ex. montage av ventilation"
                />
                {a.source && (
                  <p className="mt-1 text-xs text-muted-foreground">Hämtad från {a.source}</p>
                )}
              </div>
              <div>
                <Label>Utförare</Label>
                <Input
                  value={a.actor}
                  onChange={(e) => patch(a.id, { actor: e.target.value })}
                  placeholder="Entreprenör eller yrkesgrupp"
                />
              </div>
              <div>
                <Label>Område / utrymme</Label>
                <Input
                  value={a.area}
                  onChange={(e) => patch(a.id, { area: e.target.value })}
                  placeholder="T.ex. Plan 3 norra"
                />
              </div>
              <div>
                <Label>Etapp</Label>
                {stageOptions.length > 0 ? (
                  <Select
                    value={a.stage ? a.stage : NO_STAGE}
                    onValueChange={(v) => patch(a.id, { stage: v === NO_STAGE ? "" : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Ingen etapp" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_STAGE}>Ingen etapp</SelectItem>
                      {stageOptions.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={a.stage || ""}
                    onChange={(e) => patch(a.id, { stage: e.target.value })}
                    placeholder="T.ex. Etapp 1 – trapphus A"
                  />
                )}
              </div>
              <div>
                <Label>Typ av arbete</Label>
                <Select value={a.kind} onValueChange={(v) => patch(a.id, { kind: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIVITY_KINDS.map((k) => (
                      <SelectItem key={k} value={k}>
                        {k}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="hidden sm:block" />
              <div>
                <Label>Startdatum</Label>
                <Input
                  type="date"
                  value={a.start}
                  onChange={(e) => patch(a.id, { start: e.target.value })}
                />
              </div>
              <div>
                <Label>Slutdatum</Label>
                <Input
                  type="date"
                  value={a.end}
                  onChange={(e) => patch(a.id, { end: e.target.value })}
                />
              </div>
              <div className="flex justify-end sm:col-span-2">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Ta bort aktivitet"
                  onClick={() => onChange(activities.filter((x) => x.id !== a.id))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}

        {areaSummary.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Utrymmen med flest krockar</h4>
            <div className="space-y-1">
              {areaSummary.map((s) => {
                const active = areaFilter !== null && sameArea(s.area, areaFilter);
                return (
                  <button
                    key={s.area}
                    type="button"
                    onClick={() => setAreaFilter(active ? null : s.area)}
                    className={`flex w-full items-center justify-between gap-3 rounded-md border p-2 text-left transition-colors hover:bg-muted/60 ${
                      active ? "border-primary bg-muted/60" : ""
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{s.area}</p>
                      {s.actors.length > 0 && (
                        <p className="truncate text-xs text-muted-foreground">
                          {s.actors.join(" / ")}
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {s.high > 0 && <Badge variant="destructive">{s.high} hög</Badge>}
                      <Badge variant="secondary">{s.total} totalt</Badge>
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              Klicka på ett utrymme för att bara visa krockarna där.
            </p>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-medium">Hittade krockar</h4>
            <Badge variant={highCount > 0 ? "destructive" : "secondary"}>{conflicts.length}</Badge>
            <div className="ml-auto flex flex-wrap gap-1">
              {(
                [
                  ["alla", `Alla (${conflicts.length})`],
                  ["hög", `Hög risk (${highCount})`],
                  ["medel", `Samordna (${mediumCount})`],
                ] as Array<[SeverityFilter, string]>
              ).map(([value, label]) => (
                <Button
                  key={value}
                  size="sm"
                  variant={severityFilter === value ? "default" : "outline"}
                  onClick={() => setSeverityFilter(value)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>

          {stageOptions.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              <span className="mr-1 text-xs text-muted-foreground">Etapp:</span>
              <Button
                size="sm"
                variant={stageFilter === "alla" ? "default" : "outline"}
                onClick={() => setStageFilter("alla")}
              >
                Alla
              </Button>
              {stageOptions.map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant={stageFilter === s ? "default" : "outline"}
                  onClick={() => setStageFilter(s)}
                >
                  {s}
                </Button>
              ))}
            </div>
          )}

          {areaFilter && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>Filtrerar på utrymme: {areaFilter}</span>
              <Button size="sm" variant="ghost" onClick={() => setAreaFilter(null)}>
                Rensa
              </Button>
            </div>
          )}

          {conflicts.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Inga överlappande arbeten i samma utrymme utifrån inlagda datum.
            </p>
          )}
          {conflicts.length > 0 && visibleConflicts.length === 0 && (
            <p className="text-sm text-muted-foreground">Inga krockar matchar filtret.</p>
          )}
          {visibleConflicts.map((c) => (
            <div
              key={`${c.a.id}-${c.b.id}`}
              className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-start sm:justify-between"
            >
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <AlertTriangle
                    className={`h-4 w-4 ${c.severity === "hög" ? "text-destructive" : "text-muted-foreground"}`}
                  />
                  <span className="text-sm font-medium">
                    {c.a.name || "Aktivitet"} × {c.b.name || "Aktivitet"}
                  </span>
                  <Badge variant={c.severity === "hög" ? "destructive" : "secondary"}>
                    {c.severity === "hög" ? "Hög risk" : "Samordna"}
                  </Badge>
                  {Array.from(new Set(conflictStages(c))).map((s) => (
                    <Badge key={s} variant="outline">
                      {s}
                    </Badge>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  {c.a.area} · {c.from} – {c.to}
                  {c.a.actor || c.b.actor
                    ? ` · ${[c.a.actor, c.b.actor].filter(Boolean).join(" / ")}`
                    : ""}
                </p>
                <p className="text-sm">{c.reason}</p>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => onAddConflict(c)}>
                  Lägg till i krocklistan
                </Button>
                {onCreateCoordinationPoint && (
                  <Button size="sm" variant="ghost" onClick={() => onCreateCoordinationPoint(c)}>
                    Skapa samordningspunkt
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default ScheduleCompare;
